import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { developmentApi } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { Select } from "@/components/Select";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { getDevelopmentItUsers, type DevelopmentUser } from "@/pages/Developments/developmentUsers";
export interface ChangeAssigneeModalProps {
    isOpen: boolean;
    development: {
        id: number;
        code: string;
        title: string;
        assigned_id?: number | null;
    } | null;
    onClose: () => void;
    onSuccess: (updated: {
        assigned_id: number | null;
        assigned: { id: number; name: string; avatar?: string | null } | null;
    }) => void;
}

export const ChangeAssigneeModal: React.FC<ChangeAssigneeModalProps> = ({
    isOpen,
    development,
    onClose,
    onSuccess,
}) => {
    const { t } = useTranslation("global");
    const { user } = useAuth() as { user?: { id?: number; name?: string; email?: string; avatar?: string | null } | null };
    const { showSuccess } = useToast();
    const [selectedUserId, setSelectedUserId] = useState<string | number>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [users, setUsers] = useState<DevelopmentUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen && development) {
            setSelectedUserId(development.assigned_id || "");
            setError(null);
        }
    }, [isOpen, development]);

    useEffect(() => {
        if (isOpen && users.length === 0) {
            setLoadingUsers(true);
            getDevelopmentItUsers()
                .then(setUsers)
                .catch(() => undefined)
                .finally(() => setLoadingUsers(false));
        }
    }, [isOpen, users.length]);

    const userOptions = useMemo(() => {
        const list = users
            .filter(
                (u) =>
                    u.roles &&
                    u.roles.includes("it")
            )
            .map((u) => {
                return {
                    value: String(u.id),
                    label: u.name,
                    subtitle: u.email,
                };
            });

        list.unshift({
            value: "",
            label: t("developments.detail.unassigned"),
            description: "",
        });

        return list;
    }, [users, t]);

    const isAssignedToMe = Boolean(user?.id && Number(selectedUserId) === user.id);

    const handleAssignToMe = async (): Promise<void> => {
        if(!development || !user?.id || isAssignedToMe){
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await developmentApi.update(development.id, {
                assigned_id: user.id,
            });

            if(response.result){
                showSuccess(t("developments.modal_priority.success"));
                onSuccess({
                    assigned_id: user.id,
                    assigned: {
                        id: user.id,
                        name: user.name || user.email || "",
                        avatar: user.avatar,
                    },
                });
                onClose();
            }
            else {
                setError(response.message || t("developments.modal_priority.error"));
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : t("developments.modal_priority.error"));
        }
        finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (): Promise<void> => {
        if (!development) {
            return;
        }

        setLoading(true);
        setError(null);

        const assignedItId = selectedUserId ? Number(selectedUserId) : null;

        try {
            const res = await developmentApi.update(development.id, {
                assigned_id: assignedItId,
            });

            if (res.result) {
                showSuccess(t("developments.modal_priority.success"));
                const assignedUser = assignedItId
                    ? users.find((u) => u.id === assignedItId)
                    : null;
                onSuccess({
                    assigned_id: assignedItId,
                    assigned: assignedUser
                        ? { id: assignedUser.id, name: assignedUser.name, avatar: assignedUser.avatar }
                        : null,
                });
                onClose();
            } else {
                setError(res.message || t("developments.modal_priority.error"));
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.modal_priority.error")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("developments.detail.change_assignee")}
            maxWidth="sm"
            onSubmit={handleSubmit}
            submitLabel={t("modal.save")}
            submittingLabel={t("developments.modal_priority.saving")}
            isSubmitting={loading || loadingUsers}
        >
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center space-x-2 mb-3">
                    <Icon value="alertCircle" size={16} className="shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {development && (
                <div className="text-center mb-4">
                    <Badge value={development.code} />
                    <div className="mt-2 text-sm text-slate-800 dark:text-zinc-200">
                        {development.title}
                    </div>
                </div>
            )}

            {user?.id && (
                <div className="mb-4 flex justify-center">
                    <Button
                        variant="secondary"
                        onClick={() => void handleAssignToMe()}
                        disabled={loading || isAssignedToMe}
                        icon={<Icon value="userCheck" size={16} className="text-primary" />}
                    >
                        {t("tasks.modal_assignee.assign_to_me")}
                    </Button>
                </div>
            )}

            <Select
                value={selectedUserId}
                onChange={(val) => setSelectedUserId(val)}
                options={userOptions}
                disabled={loadingUsers}
                full={true}
                searchable={true}
            />
        </Modal>
    );
};
